import React, { useState, useMemo } from "react";
import { Search, CheckCircle2 } from "lucide-react";

export function BankSelect({
  value = "",
  bankId = null,
  onChange,
  banks = [],
  testId = "withdraw-bank",
  className = "",
  disabled = false,
  showSearch = true,
  placeholder = "-- Pilih Bank / E-Wallet --",
  t = (idText) => idText,
}) {
  const [query, setQuery] = useState("");

  // Normalize list of banks
  const normalizedBanks = useMemo(() => {
    if (!Array.isArray(banks) || banks.length === 0) return [];
    return banks.map((b) => {
      if (typeof b === "object" && b !== null) {
        return {
          bank_id: b.bank_id,
          bank_name: b.bank_name || b.name || "",
          label: b.label || `${b.bank_name || b.name} (Bank ID: ${b.bank_id})`,
          category: b.category || (b.type === 2 ? "E-Wallet" : "Bank Lainnya"),
          type: b.type || 1,
          account_type: b.account_type || (b.type === 2 ? "3" : "1"),
        };
      }
      return {
        bank_id: null,
        bank_name: String(b),
        label: String(b),
        category: "Bank Lainnya",
        type: 1,
        account_type: "1",
      };
    });
  }, [banks]);

  // Find currently selected item
  const selectedItem = useMemo(() => {
    if (!value && !bankId) return null;
    return (
      normalizedBanks.find(
        (b) =>
          (b.bank_id && bankId && b.bank_id === Number(bankId)) ||
          b.bank_name.toUpperCase() === String(value).toUpperCase() ||
          b.label.toUpperCase() === String(value).toUpperCase() ||
          (b.bank_id && String(b.bank_id) === String(value))
      ) || null
    );
  }, [normalizedBanks, value, bankId]);

  // Filter items by query
  const filteredBanks = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return normalizedBanks;
    return normalizedBanks.filter(
      (b) =>
        b.bank_name.toLowerCase().includes(q) ||
        b.label.toLowerCase().includes(q) ||
        (b.bank_id && String(b.bank_id).includes(q)) ||
        (b.category && b.category.toLowerCase().includes(q))
    );
  }, [normalizedBanks, query]);

  // Group filtered items
  const groupedBanks = useMemo(() => {
    const groups = {
      "Bank Populer": [],
      "E-Wallet": [],
      "Bank Lainnya": [],
    };
    filteredBanks.forEach((b) => {
      const cat = b.category || "Bank Lainnya";
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(b);
    });
    return groups;
  }, [filteredBanks]);

  const handleChange = (e) => {
    const val = e.target.value;
    const found = normalizedBanks.find(
      (b) => b.bank_name === val || String(b.bank_id) === String(val)
    );
    if (typeof onChange === "function") {
      onChange(val, found, e);
    }
  };

  return (
    <div className="space-y-1.5">
      {showSearch && normalizedBanks.length > 5 && (
        <div className="relative">
          <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-2.5 pointer-events-none" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Cari nama bank, e-wallet, atau Bank ID..."
            className="w-full pl-8 pr-3 py-1.5 rounded-lg border border-slate-200 outline-none text-xs text-slate-700 placeholder:text-slate-400 focus:border-blue-500 bg-white"
          />
        </div>
      )}

      <select
        value={value}
        onChange={handleChange}
        data-testid={testId}
        disabled={disabled}
        className={
          className ||
          "w-full px-3 py-2 rounded-lg border border-slate-200 outline-none text-sm text-slate-800 bg-white focus:border-blue-500"
        }
      >
        <option value="">{placeholder}</option>
        {Object.entries(groupedBanks).map(([groupName, items]) => {
          if (!items || items.length === 0) return null;
          return (
            <optgroup key={groupName} label={groupName}>
              {items.map((b) => (
                <option key={`${b.bank_name}-${b.bank_id || "raw"}`} value={b.bank_name}>
                  {b.label}
                </option>
              ))}
            </optgroup>
          );
        })}
      </select>

      {/* Selected bank ID indicator */}
      {selectedItem && selectedItem.bank_id && (
        <div className="flex items-center gap-1.5 text-[11px] text-blue-700 bg-blue-50 px-2.5 py-1 rounded-md border border-blue-200/60 font-medium">
          <CheckCircle2 className="w-3.5 h-3.5 text-blue-600 shrink-0" />
          <span>
            Bank ID: <strong className="font-mono font-bold text-slate-900">{selectedItem.bank_id}</strong>
            {" · "}
            {selectedItem.type === 2 ? "E-Wallet" : "Transfer Bank"}
            {" · "}
            {selectedItem.bank_name}
          </span>
        </div>
      )}
    </div>
  );
}

export default BankSelect;
